import { dateFormatPresets } from '@thoughtspot/date-format-util';
import { dateFormats, useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { DatePicker } from '@thoughtspot/radiant-react/widgets/date-picker';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Radio } from '@thoughtspot/radiant-react/widgets/radio';
import { Select } from '@thoughtspot/radiant-react/widgets/select';
import { TextArea } from '@thoughtspot/radiant-react/widgets/text-area';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { create } from '../../../utils/logger';
import { AnnouncementType, generateHourOptions, generateMinuteOptions, getBannerType, isValidUrl, StartTimeType, validateEndTime, validateStartTime, } from './banner-utils';
import styles from './config-announcement.module.scss';
const logger = create('config-announcement');
const DEFAULT_ANNOUNCEMENT_DATA = {
    type: AnnouncementType.INFO,
    caption: '',
    startTimeType: StartTimeType.FROM_NOW,
    customStartTime: {
        hour: '00',
        minute: '00',
    },
    endTime: {
        hour: '00',
        minute: '00',
    },
};
const HOUR_OPTIONS_GROUPS = [{ options: generateHourOptions() }];
const MINUTE_OPTIONS_GROUPS = [{ options: generateMinuteOptions() }];
export const ConfigAnnouncement = ({ onDismiss, onSave, onDelete, initialData = DEFAULT_ANNOUNCEMENT_DATA, timezone, isSaving = false, isDeleting = false, isCreateMode = false, bannerLevel, }) => {
    const { t } = useTranslation();
    const dateFormat = dateFormats[dateFormatPresets.DATE_SHORT];
    const dateFormatPlaceholder = dateFormat.toUpperCase();
    const [formData, setFormData] = useState(initialData);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkLabel, setLinkLabel] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        caption: null,
        startTime: null,
        endTime: null,
        linkLabel: null,
        linkUrl: null,
    });
    useEffect(() => {
        setFormData(initialData);
        setLinkLabel(initialData.linkLabel || '');
        setLinkUrl(initialData.linkUrl || '');
        setShowLinkInput(!!(initialData.linkLabel || initialData.linkUrl));
    }, [initialData]);
    const validateCaption = () => {
        if (!formData.caption) {
            return t('admin.home.announcement.validation.emptyCaption');
        }
        return null;
    };
    const validateLinkLabel = () => {
        if (showLinkInput && !formData.linkLabel) {
            return t('admin.home.announcement.validation.emptyLinkText');
        }
        return null;
    };
    const validateLinkUrl = () => {
        if (!showLinkInput) {
            return null;
        }
        if (!formData.linkUrl) {
            return t('admin.home.announcement.validation.emptyLinkUrl');
        }
        if (!isValidUrl(formData.linkUrl)) {
            return t('admin.home.announcement.validation.invalidLinkUrl');
        }
        return null;
    };
    const computeErrors = () => {
        return {
            caption: validateCaption(),
            startTime: validateStartTime(formData, timezone, isCreateMode, t),
            endTime: validateEndTime(formData, timezone, t),
            linkLabel: validateLinkLabel(),
            linkUrl: validateLinkUrl(),
        };
    };
    const setErrorField = (field, value) => {
        setValidationErrors(prev => ({ ...prev, [field]: value }));
    };
    const handleTypeChange = (type) => {
        setFormData(prev => ({ ...prev, type }));
    };
    const handleCaptionChange = (caption) => {
        setFormData(prev => ({ ...prev, caption }));
    };
    const handleStartTimeTypeChange = (startTimeType) => {
        setFormData(prev => ({ ...prev, startTimeType }));
    };
    const handleCustomStartDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            customStartDate: date,
        }));
        const updatedFormData = { ...formData, customStartDate: date };
        setErrorField('startTime', validateStartTime(updatedFormData, timezone, isCreateMode, t));
    };
    const handleCustomStartHourChange = (option) => {
        const hour = option.label;
        setFormData(prev => ({
            ...prev,
            customStartTime: {
                hour,
                minute: prev.customStartTime.minute,
            },
        }));
        const updatedFormData = {
            ...formData,
            customStartTime: { hour, minute: formData.customStartTime.minute },
        };
        setErrorField('startTime', validateStartTime(updatedFormData, timezone, isCreateMode, t));
    };
    const handleCustomStartMinuteChange = (option) => {
        const minute = option.label;
        setFormData(prev => ({
            ...prev,
            customStartTime: {
                hour: prev.customStartTime.hour,
                minute,
            },
        }));
        const updatedFormData = {
            ...formData,
            customStartTime: { hour: formData.customStartTime.hour, minute },
        };
        setErrorField('startTime', validateStartTime(updatedFormData, timezone, isCreateMode, t));
    };
    const handleEndDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            endDate: date,
        }));
        const updatedFormData = {
            ...formData,
            endDate: date,
        };
        setErrorField('endTime', validateEndTime(updatedFormData, timezone, t));
    };
    const handleEndHourChange = (option) => {
        const hour = option.label;
        setFormData(prev => ({
            ...prev,
            endTime: {
                hour,
                minute: prev.endTime.minute,
            },
        }));
        const updatedFormData = {
            ...formData,
            endTime: { hour, minute: formData.endTime.minute },
        };
        setErrorField('endTime', validateEndTime(updatedFormData, timezone, t));
    };
    const handleEndMinuteChange = (option) => {
        const minute = option.label;
        setFormData(prev => ({
            ...prev,
            endTime: {
                hour: prev.endTime.hour,
                minute,
            },
        }));
        const updatedFormData = {
            ...formData,
            endTime: { hour: formData.endTime.hour, minute },
        };
        setErrorField('endTime', validateEndTime(updatedFormData, timezone, t));
    };
    const handleAddLink = () => {
        setShowLinkInput(true);
    };
    const handleLinkLabelChange = (label) => {
        setLinkLabel(label);
        setFormData(prev => ({ ...prev, linkLabel: label }));
    };
    const handleLinkUrlChange = (url) => {
        setLinkUrl(url);
        setFormData(prev => ({ ...prev, linkUrl: url }));
    };
    const handleRemoveLink = () => {
        setLinkLabel('');
        setLinkUrl('');
        setShowLinkInput(false);
        setFormData(prev => ({
            ...prev,
            linkLabel: undefined,
            linkUrl: undefined,
        }));
    };
    const handleDeleteClick = () => {
        collectEvent(new UserAction(userActionTypeEnum.CONFIGURE_ANNOUNCEMENT_DELETE), {
            orgTab: bannerLevel,
            announcementType: formData.type,
            hasLink: !!formData.linkLabel && !!formData.linkUrl,
        });
        setShowDeleteConfirmation(true);
    };
    const handleDeleteConfirm = async () => {
        try {
            await onDelete();
        }
        catch (error) {
            setShowErrorAlert(true);
            logger.error('Error deleting announcement:', error);
        }
        setShowDeleteConfirmation(false);
    };
    const handleDeleteCancel = () => {
        setShowDeleteConfirmation(false);
    };
    const handleSave = async () => {
        const errors = computeErrors();
        const hasErrors = Object.values(errors).some(error => error !== null);
        if (hasErrors) {
            setValidationErrors(errors);
            return;
        }
        try {
            if (formData.startTimeType === StartTimeType.FROM_NOW) {
                formData.customStartDate = new Date();
                formData.customStartTime = {
                    hour: new Date()
                        .getHours()
                        .toString()
                        .padStart(2, '0'),
                    minute: new Date()
                        .getMinutes()
                        .toString()
                        .padStart(2, '0'),
                };
            }
            await onSave(formData);
        }
        catch (error) {
            setShowErrorAlert(true);
            logger.error('Error saving announcement:', error);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { title: t('admin.home.announcement.modal.title'), isOpen: true, onDismiss: onDismiss, size: ModalSize.MEDIUM3, confirmButton: {
                text: t('Save'),
                onConfirm: handleSave,
                isLoading: isSaving,
            }, noPadding: true, autoScrollContent: false, dismissButton: {
                text: t('CANCEL'),
                onClick: onDismiss,
            }, denyButton: isCreateMode
                ? undefined
                : {
                    text: t('DELETE'),
                    onDeny: handleDeleteClick,
                } },
            React.createElement("div", { className: styles.modalContent },
                React.createElement(Alert.Banner, { type: BannerAlertType.ERROR, message: t('error.validation.default'), isVisible: showErrorAlert, className: styles.errorBanner, onClose: () => setShowErrorAlert(false) }),
                React.createElement(Vertical, { className: styles.typeSelectionContainer },
                    React.createElement(Typography, { variant: "caption", color: Colors.base, className: styles.typeText }, t('tokenType.TYPE')),
                    React.createElement(Radio.Group, { selectedValue: formData.type, onChange: e => {
                            const target = e.target;
                            handleTypeChange(target.value);
                        }, className: styles.radioGroup },
                        React.createElement(Radio, { value: AnnouncementType.INFO, label: React.createElement(Typography, { variant: "body-normal", color: Colors.base }, t('information')), className: styles.radioButtonItem, "data-testid": "announcement-type-info-radio" }),
                        React.createElement(Radio, { value: AnnouncementType.WARNING, label: React.createElement(Typography, { variant: "body-normal", color: Colors.base }, t('WARNING')), className: styles.radioButtonItem, "data-testid": "announcement-type-warning-radio" }),
                        React.createElement(Radio, { value: AnnouncementType.ERROR, label: React.createElement(Typography, { variant: "body-normal", color: Colors.base }, t('admin.home.announcement.type.failure')), className: styles.radioButtonItem, "data-testid": "announcement-type-error-radio" }))),
                React.createElement(Vertical, null,
                    React.createElement(Horizontal, null,
                        React.createElement(Typography, { variant: "footnote", color: Colors.base }, t('admin.home.announcement.modal.caption.label')),
                        React.createElement(Typography, { variant: "caption", color: Colors.grayLight, className: styles.characterLimitText }, t('admin.home.announcement.modal.caption.characterLimit'))),
                    React.createElement(TextArea, { value: formData.caption, onChange: e => handleCaptionChange(e.target.value), onBlur: () => setErrorField('caption', validateCaption()), placeholder: t('admin.home.announcement.modal.caption.placeholder'), maxLength: 80, className: cx(styles.captionInput, {
                            [styles.hasError]: !!validationErrors.caption,
                        }), hasError: !!validationErrors.caption, helperText: validationErrors.caption, "data-testid": "announcement-caption-input" })),
                !showLinkInput && (React.createElement("div", { className: styles.addLinkButton },
                    React.createElement(Button, { type: "tertiary", text: t('admin.home.announcement.modal.link.addButton'), onClick: handleAddLink, "data-testid": "announcement-add-link-button" }))),
                showLinkInput && (React.createElement(Horizontal, { className: styles.linkInputContainer },
                    React.createElement(Vertical, null,
                        React.createElement(Horizontal, null,
                            React.createElement(Typography, { variant: "footnote", color: Colors.base }, t('admin.home.announcement.modal.link.labelPlaceholder')),
                            React.createElement(Typography, { variant: "caption", color: Colors.grayLight, className: styles.characterLimitText }, t('admin.home.announcement.modal.link.characterLimit'))),
                        React.createElement(Input.Text, { value: linkLabel, onChange: (e, value) => handleLinkLabelChange(value), onBlur: () => setErrorField('linkLabel', validateLinkLabel()), placeholder: t('admin.home.announcement.modal.link.labelInputPlaceholder'), maxLength: 20, className: styles.linkLabelInput, helperText: validationErrors.linkLabel || ' ', hasError: !!validationErrors.linkLabel, "data-testid": "announcement-link-label-input" })),
                    React.createElement(Vertical, null,
                        React.createElement(Horizontal, null,
                            React.createElement(Typography, { variant: "footnote", color: Colors.base }, t('admin.home.announcement.modal.link.urlPlaceholder'))),
                        React.createElement(Horizontal, { vAlignContent: "top" },
                            React.createElement(Input.Text, { value: linkUrl, onChange: (e, value) => handleLinkUrlChange(value), onBlur: () => setErrorField('linkUrl', validateLinkUrl()), placeholder: t('admin.home.announcement.modal.link.urlInputPlaceholder'), className: styles.linkURLInput, helperText: validationErrors.linkUrl, hasError: !!validationErrors.linkUrl, "data-testid": "announcement-link-url-input" }),
                            React.createElement(Horizontal, { vAlignContent: "top", height: "min-content" },
                                React.createElement(Button, { type: "tertiary", className: styles.trashCanButton, icon: IconID.TRASH_CAN, onClick: handleRemoveLink, "data-testid": "announcement-remove-link-button" })))))),
                React.createElement(Divider, { className: styles.divider }),
                React.createElement(Vertical, { className: styles.startTimeContainer },
                    React.createElement(Typography, { variant: "footnote", color: Colors.base, className: styles.timeTextDescription }, t('admin.home.announcement.modal.time.startTime')),
                    React.createElement(Radio.Group, { selectedValue: formData.startTimeType, onChange: e => {
                            const target = e.target;
                            handleStartTimeTypeChange(target.value);
                        }, className: styles.radioGroup },
                        React.createElement(Radio, { value: StartTimeType.FROM_NOW, label: t('admin.home.announcement.modal.time.fromNow'), className: styles.radioButtonItem, "data-testid": "announcement-start-time-from-now-radio" }),
                        React.createElement(Radio, { value: StartTimeType.CUSTOM, label: t('CUSTOM'), className: styles.radioButtonItem, "data-testid": "announcement-start-time-custom-radio" })),
                    formData.startTimeType === StartTimeType.CUSTOM && (React.createElement(Horizontal, { vAlignContent: "top", className: styles.dateTimePickerContainer, "data-testid": "announcement-start-time-picker" },
                        React.createElement(DatePicker, { selected: formData.customStartDate, onChange: handleCustomStartDateChange, dateFormat: dateFormat, placeholderText: dateFormatPlaceholder, className: styles.dateInput, hasError: !!validationErrors.startTime, underLineText: validationErrors.startTime, "data-testid": "announcement-custom-start-date-picker" }),
                        React.createElement(Horizontal, { vAlignContent: "center", height: "max-content" },
                            React.createElement(Select.Dropdown, { optionsGroups: HOUR_OPTIONS_GROUPS, selectedOptions: [
                                    formData.customStartTime.hour,
                                ], onChange: handleCustomStartHourChange, placeholder: "HH", hasError: !!validationErrors.startTime, "data-testid": "announcement-custom-start-hour-select" }),
                            React.createElement(Typography, { variant: "body-normal", color: Colors.base, className: styles.colon }, ":"),
                            React.createElement(Select.Dropdown, { optionsGroups: MINUTE_OPTIONS_GROUPS, selectedOptions: [
                                    formData.customStartTime.minute,
                                ], onChange: handleCustomStartMinuteChange, placeholder: "MM", hasError: !!validationErrors.startTime, "data-testid": "announcement-custom-start-minute-select" }),
                            React.createElement(Typography, { variant: "body-normal", color: Colors.base, className: styles.hoursSuffix }, t('hours')))))),
                React.createElement(Vertical, null,
                    React.createElement(Typography, { variant: "footnote", color: Colors.base, className: styles.timeTextDescription }, t('admin.home.announcement.modal.time.endTime')),
                    React.createElement(Horizontal, { vAlignContent: "top", className: styles.dateTimePickerContainer, "data-testid": "announcement-end-time-picker" },
                        React.createElement(DatePicker, { selected: formData.endDate, onChange: handleEndDateChange, dateFormat: dateFormat, placeholderText: dateFormatPlaceholder, className: styles.dateInput, hasError: !!validationErrors.endTime, underLineText: validationErrors.endTime, "data-testid": "announcement-end-date-picker" }),
                        React.createElement(Horizontal, { vAlignContent: "center", height: "max-content" },
                            React.createElement(Select.Dropdown, { optionsGroups: HOUR_OPTIONS_GROUPS, selectedOptions: [formData.endTime.hour], onChange: handleEndHourChange, placeholder: "HH", hasError: !!validationErrors.endTime, "data-testid": "announcement-end-hour-select" }),
                            React.createElement(Typography, { variant: "body-normal", color: Colors.base, className: styles.colon }, ":"),
                            React.createElement(Select.Dropdown, { optionsGroups: MINUTE_OPTIONS_GROUPS, selectedOptions: [formData.endTime.minute], onChange: handleEndMinuteChange, placeholder: "MM", hasError: !!validationErrors.endTime, "data-testid": "announcement-end-minute-select" }),
                            React.createElement(Typography, { variant: "body-normal", color: Colors.base, className: styles.hoursSuffix }, t('hours')))),
                    React.createElement(Typography, { variant: "caption", color: Colors.grayLight, className: styles.eventSetForText },
                        t('admin.home.announcement.modal.time.eventSetFor'),
                        "(",
                        timezone,
                        ")")),
                React.createElement(Vertical, { className: styles.previewContainer },
                    React.createElement(Typography, { variant: "footnote", color: Colors.grayLight }, t('admin.home.announcement.modal.preview.title')),
                    React.createElement(Alert.Banner, { type: getBannerType(formData.type), global: true, message: React.createElement(Horizontal, { className: styles.previewTextContainer, "data-testid": "announcement-preview-text" },
                            React.createElement(Typography, { variant: "p", color: Colors.base, ellipsis: { rows: 1 } }, formData.caption ||
                                t('admin.home.announcement.modal.preview.placeholder')),
                            formData.linkLabel &&
                                formData.linkUrl &&
                                isValidUrl(formData.linkUrl) && (React.createElement(Link, { href: formData.linkUrl, target: "_blank", rel: "noopener noreferrer", className: styles.bannerLink }, ` ${formData.linkLabel}`))), isVisible: true, className: styles.previewBanner })))),
        React.createElement(Modal, { isOpen: showDeleteConfirmation, onDismiss: handleDeleteCancel, size: ModalSize.SMALL, title: t('admin.home.announcement.modal.delete.title'), dismissButton: {
                text: t('CANCEL'),
                onClick: handleDeleteCancel,
            }, confirmButton: {
                text: t('DELETE'),
                onConfirm: handleDeleteConfirm,
                isLoading: isDeleting,
            } },
            React.createElement("div", null,
                React.createElement(Typography, { variant: "p", color: Colors.base },
                    t('admin.home.announcement.modal.delete.message'),
                    ' ',
                    "'",
                    formData.caption,
                    "'.")))));
};
//# sourceMappingURL=config-announcement.js.map